import React from 'react';
import { TableCell } from '@mui/material';
import type { AuditIntegrationCase } from '../../../types/features/storeDetails/legacyAudits';
import CalculatedValueTableCell from './CalculatedValueTableCell';
import TotalDiscrepancyDisplay from './TotalDiscrepancyDisplay';

type ColumnType = 'scratcherCounts' | 'lottoSales' | 'scratcherSales' | 'payout' | 'total';

type ColumnConfig = {
  type: ColumnType;
  columnIndex: number;
};

const COLUMN_CONFIGS: Record<AuditIntegrationCase, ColumnConfig[]> = {
  bothConnected: [
    { type: 'scratcherCounts', columnIndex: 7 },
    { type: 'scratcherSales', columnIndex: 8 },
    { type: 'lottoSales', columnIndex: 9 },
    { type: 'payout', columnIndex: 10 },
    { type: 'total', columnIndex: 11 },
  ],
  bothNotConnected: [
    { type: 'scratcherCounts', columnIndex: 7 },
    { type: 'scratcherSales', columnIndex: 8 },
  ],
  lottoConnectedOnly: [
    { type: 'scratcherCounts', columnIndex: 7 },
    { type: 'scratcherSales', columnIndex: 8 },
    { type: 'lottoSales', columnIndex: 9 },
    { type: 'payout', columnIndex: 10 },
  ],
  posConnectedOnly: [
    { type: 'scratcherCounts', columnIndex: 7 },
    { type: 'scratcherSales', columnIndex: 8 },
    { type: 'lottoSales', columnIndex: 9 },
    { type: 'payout', columnIndex: 10 },
  ],
};

type LegacyAuditConditionalColumnsProps = {
  integrationCase: AuditIntegrationCase;
  scratcherCountsDetails: any;
  lottoSalesDetails: any;
  scratcherSalesDetails: any;
  payoutDetails: any;
  totalDiscrepancy: any;
  groupBorderColor: string;
  innerBorderColor: string;
};

const LegacyAuditConditionalColumns: React.FC<LegacyAuditConditionalColumnsProps> = ({
  integrationCase,
  scratcherCountsDetails,
  lottoSalesDetails,
  scratcherSalesDetails,
  payoutDetails,
  totalDiscrepancy,
  groupBorderColor,
  innerBorderColor,
}) => {
  const config = COLUMN_CONFIGS[integrationCase];
  const innerBorderStyle = { borderRight: `1px solid ${innerBorderColor}` };
  const groupBorderStyle = { borderRight: `1px solid ${groupBorderColor}` };

  return (
    <>
      {config.map((col, index) => {
        const isGroupEnd = index === config.length - 1;
        const borderStyle = isGroupEnd ? groupBorderStyle : innerBorderStyle;
        if (col.type === 'scratcherCounts') {
          return (
            <CalculatedValueTableCell
              key={col.columnIndex}
              details={scratcherCountsDetails}
              borderRightColor={isGroupEnd ? groupBorderColor : innerBorderColor}
              formatFunction={(value) => value.toString()}
            />
          );
        }

        if (col.type === 'scratcherSales') {
          return (
            <CalculatedValueTableCell
              key={col.columnIndex}
              details={scratcherSalesDetails}
              borderRightColor={isGroupEnd ? groupBorderColor : innerBorderColor}
            />
          );
        }

        if (col.type === 'lottoSales') {
          return (
            <CalculatedValueTableCell
              key={col.columnIndex}
              details={lottoSalesDetails}
              borderRightColor={isGroupEnd ? groupBorderColor : innerBorderColor}
            />
          );
        }

        if (col.type === 'payout') {
          return (
            <CalculatedValueTableCell
              key={col.columnIndex}
              details={payoutDetails}
              borderRightColor={isGroupEnd ? groupBorderColor : innerBorderColor}
            />
          );
        }

        return (
          <TableCell key={col.columnIndex} sx={{ textAlign: 'right', ...borderStyle }}>
            {typeof totalDiscrepancy === 'object' && totalDiscrepancy && 'isNA' in totalDiscrepancy ? (
              <span style={{ color: '#666' }} title="No data available">-</span>
            ) : (
              <TotalDiscrepancyDisplay totalDiscrepancy={totalDiscrepancy} />
            )}
          </TableCell>
        );
      })}
    </>
  );
};

export default LegacyAuditConditionalColumns;
